import { debug } from "@utils/console";

/** Color-by modes offered on the dataset explore map (Map settings → Color By). */
export const EXPLORE_MAP_COLOR_MODES = ["DEFAULT", "GRADE", "WIND", "VMG%", "VMG", "STATE", "PHASE"] as const;

export type ExploreMapColorMode = (typeof EXPLORE_MAP_COLOR_MODES)[number];

/** Last color mode chosen on the explore map. Separate from the shared `colorType` so a non-map mode (scatter TACK, probability UW/DW) does not forget it. */
const EXPLORE_MAP_COLOR_STORAGE_KEY = "exploreMapColorType";

export function isExploreMapColorMode(value: string): value is ExploreMapColorMode {
  return (EXPLORE_MAP_COLOR_MODES as readonly string[]).includes(value);
}

export function readSavedExploreMapColorMode(): ExploreMapColorMode {
  if (typeof localStorage === "undefined") return "DEFAULT";
  try {
    const raw = localStorage.getItem(EXPLORE_MAP_COLOR_STORAGE_KEY);
    if (raw === null || raw === "") return "DEFAULT";
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed === "string" && isExploreMapColorMode(parsed)) return parsed;
  } catch (error) {
    debug("explore map color: could not read saved color mode", error);
  }
  return "DEFAULT";
}

export function saveExploreMapColorMode(value: string): void {
  if (!isExploreMapColorMode(value)) return;
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(EXPLORE_MAP_COLOR_STORAGE_KEY, JSON.stringify(value));
  } catch (error) {
    debug("explore map color: could not save color mode", error);
  }
}

/**
 * Color mode to show when the explore map opens.
 * A mode the map understands is kept (it is already persisted on `colorType`).
 * Anything else falls back to the last mode the user chose on this map.
 */
export function resolveExploreMapColorMode(current: string): ExploreMapColorMode {
  if (isExploreMapColorMode(current)) return current;
  return readSavedExploreMapColorMode();
}
